'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { ClipboardList, Link2, Users, Search, CheckSquare, Square, AlertTriangle, Clock, Send, X, Check, RefreshCw, Circle, Tag, UserCheck, Layers,  } from 'lucide-react';
import {
  sendWorkOrderAssignedNotification,
  sendWorkOrderStatusChangeNotification,
  sendSlaApproachingNotification,
  onEmailDelivery,
  type EmailDeliveryResult,
} from '@/lib/notifications/workOrderNotifications';
import { ToastContainer, useToast } from '@/components/ui/Toast';

// ─── Types ────────────────────────────────────────────────────────────────────

type Priority = 'P1' | 'P2' | 'P3' | 'P4';
type WOStatus = 'unassigned' | 'assigned' | 'in_progress' | 'dispatched' | 'completed';

interface WorkOrder {
  id: string;
  incidentId: string;
  incidentTitle: string;
  priority: Priority;
  slaDeadline: string;
  slaRemaining: string;
  slaBreaching: boolean;
  type: string;
  region: string;
  node: string;
  status: WOStatus;
  assigneeId: string | null;
  assigneeName: string | null;
  createdAt: string;
  estimatedDuration: string;
  customer: string;
}

interface TeamMember {
  id: string;
  name: string;
  avatar: string;
  role: string;
  department: string;
  status: 'online' | 'busy' | 'away' | 'offline';
  activeOrders: number;
  capacity: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const teamMembers: TeamMember[] = [
  { id: 'tm-1', name: 'Dawit Taddese', avatar: 'DT', role: 'Network Engineer', department: 'Network', status: 'online', activeOrders: 2, capacity: 5 },
  { id: 'tm-2', name: 'Selam Bekele', avatar: 'SB', role: 'NOC Analyst', department: 'Network', status: 'online', activeOrders: 1, capacity: 4 },
  { id: 'tm-3', name: 'Yonas Girma', avatar: 'YG', role: 'Systems Administrator', department: 'Infrastructure', status: 'busy', activeOrders: 4, capacity: 4 },
  { id: 'tm-4', name: 'Hiwot Alemu', avatar: 'HA', role: 'Security Analyst', department: 'Security', status: 'online', activeOrders: 1, capacity: 3 },
  { id: 'tm-5', name: 'Bereket Haile', avatar: 'BH', role: 'DevOps Engineer', department: 'Infrastructure', status: 'away', activeOrders: 0, capacity: 4 },
  { id: 'tm-6', name: 'Tigist Worku', avatar: 'TW', role: 'NOC Manager', department: 'Management', status: 'online', activeOrders: 0, capacity: 2 },
  { id: 'tm-7', name: 'Abebe Mekonen', avatar: 'AM', role: 'Incident Responder', department: 'Network', status: 'offline', activeOrders: 0, capacity: 5 },
];

const initialWorkOrders: WorkOrder[] = [
  { id: 'WO-1041', incidentId: 'INC-4821', incidentTitle: 'Remote node receiver offline — 18 residential units affected', priority: 'P1', slaDeadline: '09:44', slaRemaining: '8m', slaBreaching: true, type: 'Remote Node Down', region: 'Bole', node: 'RN-BOLE-APT-07', status: 'assigned', assigneeId: 'tm-1', assigneeName: 'Dawit Taddese', createdAt: '09:14', estimatedDuration: '45m', customer: 'Maple Court Apartments (MDU — 18 residents)' },
  { id: 'WO-1040', incidentId: 'INC-4820', incidentTitle: 'Base node RF link degraded — SNR below threshold', priority: 'P1', slaDeadline: '09:37', slaRemaining: '2m', slaBreaching: true, type: 'RF Link Degradation', region: 'Kirkos', node: 'BN-KIRKOS-WEST-04', status: 'unassigned', assigneeId: null, assigneeName: null, createdAt: '09:22', estimatedDuration: '30m', customer: 'EuroTelco GmbH (Business Complex — 14 tenants)' },
  { id: 'WO-1039', incidentId: 'INC-4819', incidentTitle: 'Remote node receiver unreachable — ICMP timeout all probes', priority: 'P1', slaDeadline: '09:29', slaRemaining: '15m', slaBreaching: false, type: 'Remote Node Down', region: 'Yeka', node: 'RN-YEKA-BIZ-02', status: 'in_progress', assigneeId: 'tm-2', assigneeName: 'Selam Bekele', createdAt: '09:14', estimatedDuration: '60m', customer: 'Bayview Business Park (Business Complex — 9 tenants)' },
  { id: 'WO-1038', incidentId: 'INC-4818', incidentTitle: 'Tarana base node beam-forming instability — sector oscillation', priority: 'P2', slaDeadline: '10:21', slaRemaining: '47m', slaBreaching: false, type: 'Beam-Forming Instability', region: 'Arada', node: 'BN-ARADA-NORTH-01', status: 'dispatched', assigneeId: 'tm-3', assigneeName: 'Yonas Girma', createdAt: '09:21', estimatedDuration: '90m', customer: 'PacificNet Ltd. (Business Complex — 12 tenants)' },
  { id: 'WO-1037', incidentId: 'INC-4817', incidentTitle: 'Upstream packet loss exceeds 3% — FWA backhaul link', priority: 'P2', slaDeadline: '10:19', slaRemaining: '45m', slaBreaching: false, type: 'Backhaul Packet Loss', region: 'Lideta', node: 'BN-LIDETA-SOUTH-03', status: 'unassigned', assigneeId: null, assigneeName: null, createdAt: '09:19', estimatedDuration: '30m', customer: 'Axiom Networks (Single Dwelling Business)' },
  { id: 'WO-1036', incidentId: 'INC-4816', incidentTitle: 'Remote node Rx power below minimum — signal path obstruction', priority: 'P2', slaDeadline: '10:16', slaRemaining: '42m', slaBreaching: false, type: 'Signal Degradation', region: 'Kolfe Keranio', node: 'RN-KOLFE-APT-05', status: 'unassigned', assigneeId: null, assigneeName: null, createdAt: '09:16', estimatedDuration: '120m', customer: 'Orchid Gardens Residences (MDU — 20 residents)' },
  { id: 'WO-1035', incidentId: 'INC-4815', incidentTitle: 'Base node CPU spike — scheduling overload on sector processor', priority: 'P2', slaDeadline: '10:18', slaRemaining: '44m', slaBreaching: false, type: 'CPU Overload', region: 'Nifas Silk-Lafto', node: 'BN-NIFAS-EAST-01', status: 'assigned', assigneeId: 'tm-4', assigneeName: 'Hiwot Alemu', createdAt: '09:18', estimatedDuration: '45m', customer: 'Axiom Networks (Single Dwelling Business)' },
  { id: 'WO-1034', incidentId: 'INC-4814', incidentTitle: 'Remote node hardware warning — thermal sensor above threshold', priority: 'P3', slaDeadline: '12:12', slaRemaining: '2h 38m', slaBreaching: false, type: 'Hardware Warning', region: 'Gullele', node: 'RN-GULLELE-APT-12', status: 'unassigned', assigneeId: null, assigneeName: null, createdAt: '09:12', estimatedDuration: '60m', customer: 'Lakeside Apartments (MDU — 7 residents)' },
  { id: 'WO-1033', incidentId: 'INC-4813', incidentTitle: 'Base node memory pressure — FWA session table near capacity', priority: 'P3', slaDeadline: '12:08', slaRemaining: '2h 34m', slaBreaching: false, type: 'Memory Pressure', region: 'Addis Ketema', node: 'BN-ADDISKETEMA-WEST-01', status: 'completed', assigneeId: 'tm-2', assigneeName: 'Selam Bekele', createdAt: '09:08', estimatedDuration: '30m', customer: 'BritNet Wholesale (Business Complex — 11 tenants)' },
  { id: 'WO-1032', incidentId: 'INC-4812', incidentTitle: 'QoS policy violation — residential vs business traffic remarking', priority: 'P3', slaDeadline: '11:55', slaRemaining: '2h 21m', slaBreaching: false, type: 'QoS Violation', region: 'Akaki Kality', node: 'BN-AKAKI-CENTRAL-02', status: 'completed', assigneeId: 'tm-1', assigneeName: 'Dawit Taddese', createdAt: '08:55', estimatedDuration: '20m', customer: 'RhineNet GmbH (Business Complex — 6 tenants)' },
  { id: 'WO-1031', incidentId: 'INC-4811', incidentTitle: 'Remote node NTP sync lost — FWA timing reference drift >500ms', priority: 'P4', slaDeadline: '10:30', slaRemaining: '56m', slaBreaching: false, type: 'NTP Drift', region: 'Lemi Kura', node: 'RN-LEMIKURA-BIZ-01', status: 'unassigned', assigneeId: null, assigneeName: null, createdAt: '08:30', estimatedDuration: '15m', customer: 'SunCoast ISP (Single Dwelling Business)' },
];

const priorityConfig: Record<Priority, { label: string; color: string; slaHours: string; bg: string; border: string }> = {
  P1: { label: 'Critical', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30', slaHours: '1 hour' },
  P2: { label: 'High', color: 'text-orange-400', bg: 'bg-orange-400/10', border: 'border-orange-400/30', slaHours: '4 hours' },
  P3: { label: 'Medium', color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', slaHours: '8 hours' },
  P4: { label: 'Low', color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30', slaHours: '24 hours' },
};

const statusConfig: Record<WOStatus, { label: string; color: string; bg: string }> = {
  unassigned: { label: 'Unassigned', color: 'text-muted', bg: 'bg-surface-raised' },
  assigned: { label: 'Assigned', color: 'text-blue-400', bg: 'bg-blue-400/10' },
  in_progress: { label: 'In Progress', color: 'text-primary', bg: 'bg-primary/10' },
  dispatched: { label: 'Dispatched', color: 'text-purple-400', bg: 'bg-purple-400/10' },
  completed: { label: 'Completed', color: 'text-success', bg: 'bg-success/10' },
};

const memberStatusColor: Record<string, string> = {
  online: 'bg-success',
  busy: 'bg-warning',
  away: 'bg-yellow-400',
  offline: 'bg-muted',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }: { priority: Priority }) {
  const cfg = priorityConfig[priority];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
      {priority}
    </span>
  );
}

function StatusBadgeWO({ status }: { status: WOStatus }) {
  const cfg = statusConfig[status];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color} ${cfg.bg}`}>
      {cfg.label}
    </span>
  );
}

function MemberAvatar({ member, size = 'sm' }: { member: TeamMember; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  const colors: Record<string, string> = {
    'tm-1': 'bg-primary/20 text-primary',
    'tm-2': 'bg-success/20 text-success',
    'tm-3': 'bg-warning/20 text-warning',
    'tm-4': 'bg-purple-400/20 text-purple-400',
    'tm-5': 'bg-blue-400/20 text-blue-400',
    'tm-6': 'bg-pink-400/20 text-pink-400',
    'tm-7': 'bg-orange-400/20 text-orange-400',
  };
  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-bold flex-shrink-0 ${colors[member.id] || 'bg-surface-raised text-muted'}`}>
      {member.avatar}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WorkOrderAllocationPage() {
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>(initialWorkOrders);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assignModal, setAssignModal] = useState<{ open: boolean; orderIds: string[] }>({ open: false, orderIds: [] });
  const [bulkAssignee, setBulkAssignee] = useState<string>('');
  const [dispatchConfirm, setDispatchConfirm] = useState(false);
  const [emailStatuses, setEmailStatuses] = useState<EmailDeliveryResult[]>([]);
  const { toasts, addToast, dismissToast } = useToast();

  // Broadcast sidebar badge for unassigned count
  const broadcastBadge = useCallback((orders: WorkOrder[]) => {
    const unassigned = orders.filter((w) => w.status === 'unassigned').length;
    localStorage.setItem('sidebar_badge_allocation', String(unassigned));
    window.dispatchEvent(new CustomEvent('sidebar-badge-update', { detail: { key: 'sidebar_badge_allocation', value: unassigned } }));
  }, []);

  useEffect(() => {
    broadcastBadge(workOrders);
  }, [workOrders, broadcastBadge]);

  // Listen for email delivery events
  useEffect(() => {
    const unsubscribe = onEmailDelivery((result) => {
      setEmailStatuses((prev) => {
        const filtered = prev.filter((r) => !(r.workOrderId === result.workOrderId && r.type === result.type));
        return [...filtered, result];
      });
      if (result.status === 'sent') {
        addToast(`Email notification sent for ${result.workOrderId}`, 'info', 2500);
      } else if (result.status === 'failed') {
        addToast(`Email delivery failed for ${result.workOrderId}`, 'error', 4000);
      }
    });
    return unsubscribe;
  }, [addToast]);

  const getEmailStatus = (woId: string, type: string): EmailDeliveryResult | undefined =>
    emailStatuses.find((r) => r.workOrderId === woId && r.type === type);

  const filtered = useMemo(() => {
    return workOrders.filter((wo) => {
      const q = search.toLowerCase();
      const matchSearch = !q || wo.id.toLowerCase().includes(q) || wo.incidentId.toLowerCase().includes(q) || wo.incidentTitle.toLowerCase().includes(q) || wo.node.toLowerCase().includes(q);
      const matchPriority = priorityFilter === 'all' || wo.priority === priorityFilter;
      const matchStatus = statusFilter === 'all' || wo.status === statusFilter;
      return matchSearch && matchPriority && matchStatus;
    });
  }, [workOrders, search, priorityFilter, statusFilter]);

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((w) => w.id)));
  };

  const openAssignModal = (ids: string[]) => {
    setBulkAssignee('');
    setAssignModal({ open: true, orderIds: ids });
  };

  const confirmAssign = () => {
    if (!bulkAssignee) return;
    const member = teamMembers.find((m) => m.id === bulkAssignee);
    if (!member) return;
    setWorkOrders((prev) =>
      prev.map((wo) =>
        assignModal.orderIds.includes(wo.id)
          ? { ...wo, assigneeId: member.id, assigneeName: member.name, status: 'assigned' as WOStatus }
          : wo
      )
    );

    // Send assignment notifications for each assigned work order
    assignModal.orderIds.forEach((woId) => {
      const wo = workOrders.find((w) => w.id === woId);
      if (wo) {
        sendWorkOrderAssignedNotification({
          workOrderId: wo.id,
          workOrderTitle: wo.incidentTitle,
          workOrderType: wo.type,
          priority: wo.priority,
          slaDeadline: wo.slaDeadline,
          estimatedDuration: wo.estimatedDuration,
          assigneeName: member.name,
          assignerName: 'NOC Manager',
          incidentId: wo.incidentId,
          node: wo.node,
          woRegion: wo.region,
          woCustomer: wo.customer,
          recipients: ['team@netopsai.com'],
        });
        // Send SLA approaching notification if SLA is breaching
        if (wo.slaBreaching) {
          sendSlaApproachingNotification({
            workOrderId: wo.id,
            workOrderTitle: wo.incidentTitle,
            incidentId: wo.incidentId,
            priority: wo.priority,
            slaDeadline: wo.slaDeadline,
            slaRemaining: wo.slaRemaining,
            assigneeName: member.name,
            node: wo.node,
            woRegion: wo.region,
            woCustomer: wo.customer,
            woStatus: 'assigned',
            recipients: ['team@netopsai.com'],
          });
        }
      }
    });

    setSelected(new Set());
    setAssignModal({ open: false, orderIds: [] });
    addToast(`${assignModal.orderIds.length} work order(s) assigned to ${member.name}`, 'success');
  };

  const bulkDispatch = () => {
    const ids = Array.from(selected);
    const assignedIds = ids.filter((id) => {
      const wo = workOrders.find((w) => w.id === id);
      return wo && wo.assigneeId;
    });
    if (assignedIds.length === 0) {
      addToast('Select assigned work orders to dispatch', 'warning');
      return;
    }
    setWorkOrders((prev) =>
      prev.map((wo) =>
        assignedIds.includes(wo.id) ? { ...wo, status: 'dispatched' as WOStatus } : wo
      )
    );

    // Send status change notifications for dispatched orders
    assignedIds.forEach((woId) => {
      const wo = workOrders.find((w) => w.id === woId);
      if (wo) {
        sendWorkOrderStatusChangeNotification({
          workOrderId: wo.id,
          workOrderTitle: wo.incidentTitle,
          oldStatus: wo.status,
          newStatus: 'dispatched',
          updatedBy: 'NOC Manager',
          node: wo.node,
          woRegion: wo.region,
          priority: wo.priority,
          slaDeadline: wo.slaDeadline,
          recipients: ['team@netopsai.com'],
        });
      }
    });

    setSelected(new Set());
    setDispatchConfirm(false);
    addToast(`${assignedIds.length} work order(s) dispatched`, 'success');
  };

  // Stats
  const stats = useMemo(() => ({
    total: workOrders.length,
    unassigned: workOrders.filter((w) => w.status === 'unassigned').length,
    breaching: workOrders.filter((w) => w.slaBreaching).length,
    dispatched: workOrders.filter((w) => w.status === 'dispatched').length,
  }), [workOrders]);

  return (
    <AppLayout>
      <div className="p-6 xl:p-8 max-w-screen-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <ClipboardList size={22} className="text-primary" />
              Work Order Allocation
            </h1>
            <p className="text-sm text-muted mt-0.5">
              Incident-linked work orders · Team assignment · SLA-aware dispatch
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <>
                <button
                  onClick={() => openAssignModal(Array.from(selected))}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/15 text-primary text-sm font-medium hover:bg-primary/25 transition-colors"
                >
                  <UserCheck size={15} />
                  Assign ({selected.size})
                </button>
                <button
                  onClick={() => setDispatchConfirm(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/15 text-success text-sm font-medium hover:bg-success/25 transition-colors"
                >
                  <Send size={15} />
                  Bulk Dispatch ({selected.size})
                </button>
              </>
            )}
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-sm text-muted hover:text-foreground hover:border-primary/40 transition-colors">
              <RefreshCw size={14} />
              Refresh
            </button>
          </div>
        </div>

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Work Orders', value: stats.total, icon: Layers, color: 'text-primary', bg: 'bg-primary/10' },
            { label: 'Unassigned', value: stats.unassigned, icon: Circle, color: 'text-orange-400', bg: 'bg-orange-400/10' },
            { label: 'SLA Breaching', value: stats.breaching, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-400/10' },
            { label: 'Dispatched', value: stats.dispatched, icon: Send, color: 'text-success', bg: 'bg-success/10' },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-surface border border-border rounded-xl p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center flex-shrink-0`}>
                <kpi.icon size={18} className={kpi.color} />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                <p className="text-xs text-muted">{kpi.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Main content: Table + Team Panel */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-5">

          {/* Work Order Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 p-4 border-b border-border">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search WO, incident, node…"
                  className="w-full pl-8 pr-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-foreground placeholder:text-muted focus:outline-none focus:border-primary/50"
                />
              </div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
                className="px-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary/50"
              >
                <option value="all">All Priorities</option>
                <option value="P1">P1 – Critical</option>
                <option value="P2">P2 – High</option>
                <option value="P3">P3 – Medium</option>
                <option value="P4">P4 – Low</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 bg-surface-raised border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary/50"
              >
                <option value="all">All Statuses</option>
                <option value="unassigned">Unassigned</option>
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="dispatched">Dispatched</option>
                <option value="completed">Completed</option>
              </select>
              <span className="text-xs text-muted ml-auto">{filtered.length} orders</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-raised/50">
                    <th className="px-4 py-2.5 text-left w-10">
                      <button onClick={toggleAll} className="text-muted hover:text-foreground">
                        {selected.size === filtered.length && filtered.length > 0
                          ? <CheckSquare size={15} className="text-primary" />
                          : <Square size={15} />}
                      </button>
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">WO / Incident</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">Priority</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">SLA</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">Node / Region</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">Assignee</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">Status</th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((wo) => {
                    const isSelected = selected.has(wo.id);
                    const pCfg = priorityConfig[wo.priority];
                    const emailSt = getEmailStatus(wo.id, 'work_order_assigned') || getEmailStatus(wo.id, 'work_order_status_change');
                    return (
                      <tr
                        key={wo.id}
                        className={`border-b border-border/50 transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-surface-raised/40'}`}
                      >
                        <td className="px-4 py-3">
                          <button onClick={() => toggleRow(wo.id)} className="text-muted hover:text-foreground">
                            {isSelected ? <CheckSquare size={15} className="text-primary" /> : <Square size={15} />}
                          </button>
                        </td>
                        <td className="px-3 py-3 min-w-[220px]">
                          <div className="flex items-start gap-2">
                            <Link2 size={13} className="text-muted mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-mono text-xs text-primary font-semibold">{wo.id}</p>
                              <p className="text-xs text-muted font-mono">{wo.incidentId}</p>
                              <p className="text-xs text-foreground mt-0.5 leading-tight line-clamp-1">{wo.incidentTitle}</p>
                              {/* Email delivery status indicator */}
                              {emailSt && (
                                <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full
                                  ${emailSt.status === 'sent' ? 'bg-success/10 text-success' :
                                    emailSt.status === 'failed'? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                    emailSt.status === 'sent' ? 'bg-success' :
                                    emailSt.status === 'failed'? 'bg-danger' : 'bg-primary animate-pulse'}`} />
                                  {emailSt.status === 'sent' ? 'Email sent' : emailSt.status === 'failed' ? 'Email failed' : 'Sending…'}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="space-y-1">
                            <PriorityBadge priority={wo.priority} />
                            <p className="text-[10px] text-muted">SLA: {pCfg.slaHours}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <div className={`flex items-center gap-1 ${wo.slaBreaching ? 'text-red-400' : 'text-muted'}`}>
                            <Clock size={12} />
                            <span className="text-xs font-medium">{wo.slaDeadline}</span>
                          </div>
                          <p className={`text-[10px] mt-0.5 ${wo.slaBreaching ? 'text-red-400 font-semibold' : 'text-muted'}`}>
                            {wo.slaBreaching ? '⚠ Breaching' : `${wo.slaRemaining} left`}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="text-xs font-medium text-foreground">{wo.node}</p>
                          <p className="text-[10px] text-muted">{wo.region}</p>
                        </td>
                        <td className="px-3 py-3">
                          {wo.assigneeName ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                {wo.assigneeName.split(' ').map((n) => n[0]).join('')}
                              </div>
                              <span className="text-xs text-foreground truncate max-w-[90px]">{wo.assigneeName}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted italic">Unassigned</span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <StatusBadgeWO status={wo.status} />
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openAssignModal([wo.id])}
                              title="Assign"
                              className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-primary/10 transition-colors"
                            >
                              <UserCheck size={13} />
                            </button>
                            {wo.assigneeId && wo.status !== 'dispatched' && wo.status !== 'completed' && (
                              <button
                                onClick={() => {
                                  setWorkOrders((prev) =>
                                    prev.map((w) => w.id === wo.id ? { ...w, status: 'dispatched' } : w)
                                  );
                                  addToast(`${wo.id} dispatched successfully`, 'success');
                                  sendWorkOrderStatusChangeNotification({
                                    workOrderId: wo.id,
                                    workOrderTitle: wo.incidentTitle,
                                    oldStatus: wo.status,
                                    newStatus: 'dispatched',
                                    updatedBy: 'NOC Manager',
                                    node: wo.node,
                                    woRegion: wo.region,
                                    priority: wo.priority,
                                    slaDeadline: wo.slaDeadline,
                                    recipients: ['team@netopsai.com'],
                                  });
                                }}
                                title="Dispatch"
                                className="p-1.5 rounded-md text-muted hover:text-success hover:bg-success/10 transition-colors"
                              >
                                <Send size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-muted text-sm">
                        No work orders match the current filters
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Team Panel */}
          <div className="space-y-4">
            {/* SLA Priority Map */}
            <div className="bg-surface border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Tag size={14} className="text-primary" />
                Priority / SLA Map
              </h3>
              <div className="space-y-2">
                {(Object.entries(priorityConfig) as [Priority, typeof priorityConfig.P1][]).map(([p, cfg]) => (
                  <div key={p} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${cfg.bg} ${cfg.border}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold ${cfg.color}`}>{p}</span>
                      <span className="text-xs text-muted">{cfg.label}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted">
                      <Clock size={11} />
                      {cfg.slaHours}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Capacity */}
            <div className="bg-surface border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Users size={14} className="text-primary" />
                Team Capacity
              </h3>
              <div className="space-y-3">
                {teamMembers.map((member) => {
                  const pct = Math.round((member.activeOrders / member.capacity) * 100);
                  const barColor = pct >= 100 ? 'bg-red-400' : pct >= 75 ? 'bg-warning' : 'bg-success';
                  return (
                    <div key={member.id} className="space-y-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <MemberAvatar member={member} size="sm" />
                            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${memberStatusColor[member.status]}`} />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-foreground leading-tight">{member.name.split(' ')[0]}</p>
                            <p className="text-[10px] text-muted leading-tight">{member.role}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted">{member.activeOrders}/{member.capacity}</span>
                      </div>
                      <div className="h-1.5 bg-surface-raised rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${barColor}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
                <UserCheck size={16} className="text-primary" />
                Assign Work Order{assignModal.orderIds.length > 1 ? 's' : ''}
              </h2>
              <button onClick={() => setAssignModal({ open: false, orderIds: [] })} className="text-muted hover:text-foreground">
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-muted mb-4">
              Assigning <span className="text-foreground font-medium">{assignModal.orderIds.length}</span> work order(s):
              <span className="font-mono text-primary ml-1">{assignModal.orderIds.join(', ')}</span>
            </p>

            <div className="space-y-2 mb-5">
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Select Team Member</p>
              {teamMembers.map((member) => {
                const pct = Math.round((member.activeOrders / member.capacity) * 100);
                const isFull = pct >= 100;
                return (
                  <button
                    key={member.id}
                    disabled={isFull || member.status === 'offline'}
                    onClick={() => setBulkAssignee(member.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left
                      ${bulkAssignee === member.id ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/40 hover:bg-surface-raised'}
                      ${(isFull || member.status === 'offline') ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="relative">
                      <MemberAvatar member={member} size="sm" />
                      <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-surface ${memberStatusColor[member.status]}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{member.name}</p>
                      <p className="text-xs text-muted">{member.role}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-xs text-muted">{member.activeOrders}/{member.capacity}</p>
                      {isFull && <p className="text-[10px] text-red-400">Full</p>}
                    </div>
                    {bulkAssignee === member.id && <Check size={14} className="text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setAssignModal({ open: false, orderIds: [] })}
                className="flex-1 px-4 py-2 rounded-lg border border-border text-sm text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAssign}
                disabled={!bulkAssignee}
                className="flex-1 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Dispatch Confirm */}
      {dispatchConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-success/15 flex items-center justify-center">
                <Send size={18} className="text-success" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground">Bulk Dispatch</h2>
                <p className="text-xs text-muted">Send {selected.size} work orders to assignees</p>
              </div>
            </div>
            <p className="text-sm text-muted mb-5">
              Only assigned work orders will be dispatched. Unassigned orders will be skipped.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDispatchConfirm(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-border text-sm text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={bulkDispatch}
                className="flex-1 px-4 py-2 rounded-lg bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors"
              >
                Dispatch Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </AppLayout>
  );
}
